/**
 * @author jakubmaslon
 * @since 2021-9-2
 */

import * as React from "react";
import styled from "styled-components";

import { rem } from "@ui/helpers";

/* eslint-disable no-mixed-operators */
const axisYTemplate = (min: number, i: number): string => `
    &:nth-child(${(i - min) * 2 + 1}) {
      .fc-timegrid-slot {
        &::after {
          content: "${i}:00";
        }
      }
    }
  `;
/* eslint-enable no-mixed-operators */

const createAxisY = (min: number, max: number) => {
  const styles = [];

  for (let i = min; i <= max; i++) {
    styles.push(axisYTemplate(min, i));
  }

  return styles.join("");
};

interface Props {
  children: any;
  min: number;
  max: number;
  className?: string;
}

const FullCalendarShared = (props: Props): React.ReactElement => (
  <FullCalendarStyles
    min={props.min}
    max={props.max}
    className={props.className}
  >
    {props.children}
  </FullCalendarStyles>
);

export { FullCalendarShared };

FullCalendarShared.displayName = "FullCalendarShared";

// *** CALENDAR ***
const FullCalendarStyles = styled.div<{ min: number; max: number }>`
  background: ${props => props.theme.palette.white};
  padding: ${props => props.theme.margins.base_x2}
    ${props => props.theme.margins.base_x3};

  // *** GRID ***
  // grid container
  .fc-scrollgrid {
    border: none;
  }

  .fc-scrollgrid-section {
    > td {
      border: none;
    }
  }

  .fc-scroller-harness {
    overflow: visible;
  }

  .fc-timegrid-body {
    margin-top: ${props => props.theme.margins.base_x2};
  }

  .fc-timegrid-slots {
    border: ${rem(1)} solid ${props => props.theme.palette.grey100};
  }

  col {
    width: 0 !important;
  }

  // *** HEADER ***
  // header section
  .fc-scrollgrid thead {
    position: relative;

    ${props => props.theme.breakpoints.tablet} {
      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 50%;
        width: calc(100% + ${props => props.theme.margins.base_x6} * 2);
        height: ${rem(23)};
        background: ${props => props.theme.palette.grey600};
        transform: translateX(-50%);
        z-index: -1;
      }
    }
  }

  // header row
  .fc-col-header {
    color: ${props => props.theme.palette.white};
  }

  // header cell
  .fc-col-header-cell {
    border-left: ${rem(1)} solid ${props => props.theme.palette.white};
    border-right: ${rem(1)} solid ${props => props.theme.palette.white};
    border-bottom: none;
    border-radius: ${props => props.theme.border.radius.base};
    background: ${props => props.theme.palette.grey600};

    ${props => props.theme.breakpoints.tablet} {
      border: none;
      border-radius: 0;
      background: ${props => props.theme.palette.grey600};
    }
  }

  // header cell
  .fc-col-header-cell-cushion {
    font-size: ${props => props.theme.fonts.sizes.caption};

    ${props => props.theme.breakpoints.tablet} {
      font-weight: ${props => props.theme.fonts.weights.normal};
      font-size: ${props => props.theme.fonts.sizes.caption};
    }
  }

  // header axis cell
  .fc-timegrid-axis {
    visibility: hidden;
    border: none;
  }

  .fc-timegrid-slot-label.fc-timegrid-slot-label {
    border: none;
  }

  // *** COLUMN ***
  // header today
  .fc-day-today {
    background: ${props => props.theme.palette.grey800};
  }

  // column today
  .fc-timegrid-col.fc-day-today {
    background: ${props => props.theme.palette.grey20};
  }

  // time column axe
  .fc-timegrid-slot-label-cushion {
    display: none;

    ${props => props.theme.breakpoints.tablet} {
      font-size: ${props => props.theme.fonts.sizes.caption};
      color: ${props => props.theme.palette.grey600};
      transform: translate(-${props => props.theme.margins.quarter}, -50%);
    }
  }

  // column
  .fc-timegrid-col {
    position: relative;

    &:first-child {
      border: none;
    }
  }

  .empty-slots-labels {
    position: relative;
    top: 50%;
    display: flex;
    justify-content: space-around;
    flex-basis: 1;

    ${props => props.theme.breakpoints.tablet} {
      display: none;
    }
  }

  // *** ROW ***
  td.fc-timegrid-slot.fc-timegrid-slot-lane.fc-timegrid-slot-minor {
    height: ${props => props.theme.margins.base_x2};
    border-top: none;

    ${props => props.theme.breakpoints.tablet} {
      height: ${props => props.theme.margins.base_x4};
      border-top: ${rem(1)} solid ${props => props.theme.palette.grey50};
    }
  }

  .fc-timegrid-slots tbody > tr {
    ${props => props.theme.breakpoints.tablet} {
      ${props => createAxisY(props.min, props.max)}
    }
  }

  .fc-timegrid-slot {
    position: relative;
    height: ${props => props.theme.margins.base_x2};
    font-size: ${props => props.theme.fonts.sizes.caption};

    ${props => props.theme.breakpoints.tablet} {
      height: ${props => props.theme.margins.base_x4};
    }

    &:first-child {
      &::after {
        right: ${props => props.theme.margins.base_x2};
      }
    }

    &:last-child {
      &::after {
        left: calc(100% + ${props => props.theme.margins.base_x2});
      }
    }

    &::after {
      position: absolute;
      top: 0;
      transform: translateY(-50%);
    }
  }

  // *** EVENT ***
  .fc-timegrid-col-events {
    margin: 0;
  }

  .fc-timegrid-event {
    border-radius: 0;
  }

  .fc-timegrid-event .fc-event-main {
    padding: 0;
  }

  .fc-event-main {
    padding: 0;
  }

  .fc-v-event {
    border: none;
  }
`;
